import React, { useState, useEffect } from 'react';
import { Newspaper, TrendingUp, FileText, Loader2 } from 'lucide-react';
import { useTheme } from '../components/ThemeContext';

interface NewsItem {
  symbol: string | null;
  publisher: string;
  title: string;
  image: string;
  site: string;
  text: string;
  url: string;
  publishedDate: string;
}

type TabType = 'markets' | 'stocks' | 'press';


const News: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('markets');
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  // Fetch news based on active tab
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const base = (import.meta.env.VITE_APP_API_URL as string) || 'http://localhost:5000';
        let endpoint = '';
        
        switch (activeTab) {
          case 'markets':
            endpoint = '/api/news/general-latest';
            break;
          case 'press':
            endpoint = '/api/news/press-releases';
            break;
          case 'stocks':
            endpoint = '/api/news/stock-latest';
            break;
        }
        
        const response = await fetch(`${base}${endpoint}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setNews(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [activeTab]);

  const getTimeAgo = (publishedDate: string): string => {
    const now = new Date();
    const published = new Date(publishedDate);
    const diffMs = now.getTime() - published.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      return '1 day ago';
    } else {
      return `${diffDays} days ago`;
    }
  };

  const tabs = [
    { id: 'markets' as TabType, label: 'Markets', icon: TrendingUp },
    { id: 'stocks' as TabType, label: 'All Stocks', icon: Newspaper },
    { id: 'press' as TabType, label: 'Press Releases', icon: FileText },
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-theme-primary' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-theme-primary' : 'text-gray-900'}`} style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif' }}>
            Stock Market News
          </h1>
        </div>

        {/* Tabs */}
        <div className={`border-b mb-6 ${theme === 'dark' ? 'border-theme-tertiary' : 'border-gray-200'}`}>
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 py-3 outline-none px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? `active-border ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`
                      : `border-transparent ${theme === 'dark' ? 'text-theme-muted hover:text-gray-200 hover:border-theme-tertiary' : 'text-gray-600 hover:text-gray-900 hover:border-gray-300'}`
                    }
                  `}
                  style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif' }}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content - Full Width */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {news.map((item, idx) => (
                <a
                  key={idx}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <article className={`rounded-lg overflow-hidden hover:shadow-lg transition-shadow border ${theme === 'dark' ? 'bg-theme-secondary border-theme-tertiary' : 'bg-white border-gray-200'}`}>
                    <div className="flex flex-col sm:flex-row">
                      {item.image && (
                        <div className={`sm:w-64 h-48 sm:h-auto flex-shrink-0 ${theme === 'dark' ? 'bg-theme-tertiary' : 'bg-gray-100'}`}>
                          <img
                            src={item.image}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="p-5 flex-1">
                        <div className={`flex items-center gap-2 text-xs mb-2 ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-600'}`} style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                          <span>{getTimeAgo(item.publishedDate)}</span>
                          <span>•</span>
                          <span className="font-medium">{item.publisher}</span>
                        </div>
                        <h2 className={`text-lg font-semibold mb-2 transition-colors ${theme === 'dark' ? 'text-theme-primary group-hover:text-blue-400' : 'text-gray-900 group-hover:text-yellow-600'}`} style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                          {item.title}
                        </h2>
                        {item.text && (
                          <p className={`text-sm line-clamp-2 ${theme === 'dark' ? 'text-theme-muted' : 'text-gray-600'}`} style={{ fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                            {item.text}
                          </p>
                        )}
                      </div>
                    </div>
                  </article>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default News;
